import { FC, useMemo, useRef } from 'react';
import {
	Alert,
	ModalMainContent,
	ModalWrapper,
	SelectFormControl,
} from '../../../shared-components';
import { useFormik } from 'formik';
import { app, createDeployment } from '../../../../@xmcloud/core/messages/en';
import {
	useGetProjectEnvironmentsList,
	useGetProjectList,
} from '../../../services/projects';
import { createDeploymentSchema } from '../../../../@xmcloud/yup-validations/createDeployment';

const {
	createTitle,
	selectProject,
	selectEnvironment,
	select,
	noEnvironment,
	noEnvInfo,
} = createDeployment;

interface Props {
	openModal: boolean;
	setOpenModal: (a: boolean) => void;
	onSubmit: (values: any) => void;
	isLoading?: boolean;
}

const PROJECT = 'project';
const ENVIRONMENTID = 'environmentId';
const ENV_TYPE = 'envType';

const CreateDeploymentForm: FC<Props> = ({
	openModal,
	setOpenModal,
	onSubmit,
	isLoading = false,
}) => {
	const initialValues = useRef({
		project: '',
		environmentId: '',
		envType: '',
	}).current;

	const { data: projects, isLoading: projectIsLoading } = useGetProjectList();

	const projectOptions =
		projects?.data.map((project) => ({
			label: project.name,
			value: project.id,
		})) || [];

	const validationSchema = createDeploymentSchema();

	const {
		errors,
		values,
		handleSubmit: formikHandleSubmit,
		setValues,
		setFieldTouched,
		touched,
	} = useFormik({
		enableReinitialize: true,
		initialValues,
		validationSchema,
		validateOnBlur: true,
		onSubmit: async (values) => {
			onSubmit(values);
		},
	});

	const projectName = useMemo(
		() => projects?.data?.find((p) => p.id === values.project)?.name || '',
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[values.project],
	);

	const { data: environments, isLoading: environmentIsLoading } =
		useGetProjectEnvironmentsList({
			projectId: values.project,
			enabled: !!values.project,
		});

	const environmentOptions =
		environments?.data.map((env) => ({
			label: env.name,
			value: env.id,
			type: env?.type,
		})) || [];

	const environmentLength = environmentOptions.length;

	return (
		<ModalWrapper
			isOpen={openModal}
			title={createTitle}
			onClose={() => setOpenModal(false)}
		>
			<form
				data-testid="create-deployment-form"
				onSubmit={formikHandleSubmit}
			>
				<ModalMainContent
					onClose={() => setOpenModal(false)}
					isLoading={isLoading}
					rightButtonText={app.deploy}
					isDisabled={!values.project || !environmentLength}
				>
					<SelectFormControl
						error={errors.project}
						isInvalid={Boolean(errors.project && touched.project)}
						isLoading={projectIsLoading}
						label={selectProject}
						name={PROJECT}
						onChange={(e: any) => {
							setFieldTouched(ENVIRONMENTID, false);
							setValues((prev) => ({
								...prev,
								[PROJECT]: e.value,
								[ENVIRONMENTID]: '',
							}));
						}}
						options={projectOptions}
						currentValue={values.project}
						onFocus={() => setFieldTouched(PROJECT, true)}
						pt={-2}
					/>
					<SelectFormControl
						error={errors.environmentId}
						isInvalid={
							Boolean(values.project) &&
							Boolean(errors.environmentId) &&
							Boolean(touched.environmentId)
						}
						isLoading={environmentIsLoading}
						label={selectEnvironment}
						name={ENVIRONMENTID}
						onChange={(e: any) => {
							setValues((prev) => ({
								...prev,
								[ENVIRONMENTID]: e.value,
								[ENV_TYPE]: e.type,
							}));
						}}
						options={environmentOptions}
						currentValue={values.environmentId}
						isDisabled={!values.project || !environmentLength}
						onFocus={() => setFieldTouched(ENVIRONMENTID, true)}
						placeholder={
							!values.project || environmentIsLoading
								? select
								: !environmentLength
									? noEnvironment
									: select
						}
					/>
					{values.project &&
						!environmentLength &&
						!environmentIsLoading && (
							<Alert
								description={noEnvInfo}
								isVisibleCloseBtn={false}
								status="warning"
								marginTop={4}
								boldText={projectName}
								minWidth="100%"
								boldTextTransform="none"
							/>
						)}
				</ModalMainContent>
			</form>
		</ModalWrapper>
	);
};

export default CreateDeploymentForm;
